/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { NoDataEmptyState } from '@carbon/ibm-products';
import { TrashCan, CheckmarkFilled } from '@carbon/react/icons';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  Pagination,
  OverflowMenu,
  OverflowMenuItem
} from 'carbon-components-react';
import cx from 'classnames';
import React, { useEffect, useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../store';
import { deleteDocs } from '../store/actions';
import {
  selectFiles,
  selectDeleteFilesLoading,
  selectListFilesLoading
} from '../store/selectors';
import {
  ColumnDirections,
  ROWS_PER_PAGE_DEFAULT,
  ROWS_PER_PAGE_OPTIONS
} from '../util/constants';
import { messages } from './messages';

import './ElasticSearchDocuments.scss';

const {
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch
} = DataTable;

interface ElasticSearchDocumentsProps {
  assistantId: string;
  showPanel: () => void;
  deletedFiles: (num) => void;
  uploadPanel: boolean;
  readOnly: boolean;
  skillId: string;
}

const ElasticSearchDocumentHeaderKeys = {
  TITLE: 'filename',
  DATE: 'ingest_timestamp',
  STATUS: 'status',
  OVERFLOW: 'overflow'
};
export const ElasticSearchDocuments = ({
  assistantId,
  showPanel,
  deletedFiles,
  uploadPanel,
  skillId,
  readOnly
}: ElasticSearchDocumentsProps) => {
  const dispatch = useAppDispatch();
  const files = useAppSelector(selectFiles);
  const deleteLoading = useAppSelector(selectDeleteFilesLoading);
  const listFilesLoading = useAppSelector(selectListFilesLoading);
  const { formatMessage: t } = useIntl();
  const [filterWord, setFilterWord] = useState('');
  const [allRows, setAllRows] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [displayedRows, setDisplayedRows] = useState([]);
  const [showOverflowOnTop, setShowOverflowOnTop] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const [sort, setSort] = useState({
    key: ElasticSearchDocumentHeaderKeys.TITLE,
    direction: ColumnDirections.NONE
  });

  useEffect(() => {
    const rows = [];
    files.forEach(file => {
      rows.push({
        id: file?._id,
        filename: file?._source?.filename || t(messages.noTitle),
        ingest_timestamp: new Date(
          file?._source?.ingest_timestamp
        ).toLocaleString(),
        status: (
          <span className="ElasticSearchDocuments__status">
            <CheckmarkFilled
              size={16}
              className="ElasticSearchDocuments__status-icon-no-error"
            />
            <FormattedMessage {...messages.ready} />
          </span>
        )
      });
    });
    setDisplayedRows(rows);
    setAllRows(rows);
  }, [files, t]);

  const toggleSortOrder = headerClicked => {
    let nextDirection = ColumnDirections.ASC;
    if (headerClicked === sort.key) {
      const directions = [
        ColumnDirections.ASC,
        ColumnDirections.DESC,
        ColumnDirections.NONE
      ];
      nextDirection = directions[(directions.indexOf(sort.direction) + 1) % 3];
    }

    const newSort = { key: headerClicked, direction: nextDirection };
    const sortedFiltered = getSortedFiltered(newSort, filterWord, allRows);

    setPage(1);
    setSort(newSort);
    setDisplayedRows(sortedFiltered.slice(0, rowsPerPage));
  };

  const headers = [
    {
      key: ElasticSearchDocumentHeaderKeys.TITLE,
      header: t(messages.documentTitle),
      isSortable: true,
      colSpan: 6,
      isSorted: sort?.key === ElasticSearchDocumentHeaderKeys.TITLE,
      onClick: () => toggleSortOrder(ElasticSearchDocumentHeaderKeys.TITLE),
      sortDirection:
        sort?.key === ElasticSearchDocumentHeaderKeys.TITLE
          ? sort?.direction
          : ColumnDirections.NONE
    },
    {
      key: ElasticSearchDocumentHeaderKeys.DATE,
      header: t(messages.dateUploaded),
      isSortable: true,
      isSorted: sort?.key === ElasticSearchDocumentHeaderKeys.DATE,
      onClick: () => toggleSortOrder(ElasticSearchDocumentHeaderKeys.DATE),
      sortDirection:
        sort?.key === ElasticSearchDocumentHeaderKeys.DATE
          ? sort?.direction
          : ColumnDirections.NONE
    },
    {
      key: ElasticSearchDocumentHeaderKeys.STATUS,
      header: t(messages.status),
      isSortable: true,
      isSorted: sort?.key === ElasticSearchDocumentHeaderKeys.STATUS,
      onClick: () => toggleSortOrder(ElasticSearchDocumentHeaderKeys.STATUS),
      sortDirection:
        sort?.key === ElasticSearchDocumentHeaderKeys.STATUS
          ? sort?.direction
          : ColumnDirections.NONE
    },
    {
      colSpan: 2,
      header: '',
      isSortable: false,
      key: ElasticSearchDocumentHeaderKeys.OVERFLOW
    }
  ];

  const handleRowSelect = row => {
    const isThere = selectedData.some(i => i === row.id);
    const revisedSelectedData = isThere
      ? selectedData.filter(i => i !== row.id)
      : selectedData.concat(row.id);
    setSelectedData(revisedSelectedData);
  };

  const handleSelectAll = () => {
    let ids = [];
    const selectedDataLength = selectedData.length;
    const onlyActions = displayedRows.filter(item => !item?.collection);
    // if any remain unselected, add them
    if (selectedDataLength >= 0 && selectedDataLength < onlyActions.length) {
      for (const key in onlyActions) {
        ids.push(onlyActions[key].id);
      }
    } else {
      // if all selected before, deselect all
      ids = [];
    }
    setSelectedData(ids);
  };

  const onDelete = async row => {
    await dispatch(deleteDocs({ skillId, assistantId, docIds: [row.id] }));
    deletedFiles(1);
  };

  const batchDelete = async () => {
    await dispatch(deleteDocs({ skillId, assistantId, docIds: selectedData }));
    deletedFiles(selectedData.length);
    setSelectedData([]);
  };

  const setMenuPlacement = evt => {
    if (evt.type === 'click' || evt.key === 'Enter') {
      // where the user clicked or entered, relative to the viewed area
      const triggerLoc = evt.currentTarget.getBoundingClientRect().top;

      // WARNING if the no. of menuitems changes, set the value to new height of popup
      const heightOfPopup = 120;

      const tableArea = document
        .getElementsByTagName('tbody')[0]
        .getBoundingClientRect();
      if (triggerLoc - tableArea.top < heightOfPopup) {
        // popup can't fit above, so show it below, even if user must scroll
        setShowOverflowOnTop(false);
      } else {
        // popup can fit above, but we still want to show it below if it will fit without scrolling
        const cantFitBelow = tableArea.height - triggerLoc < heightOfPopup;
        setShowOverflowOnTop(cantFitBelow);
      }
    }
  };

  const getSortFunction = useCallback(
    key => {
      switch (key) {
        case ElasticSearchDocumentHeaderKeys.TITLE:
          return (a, b) => {
            // if action is Untitled, title is null
            const aTempTitle = a.title ? a.title : t(messages.noTitle);
            const bTempTitle = b.title ? b.title : t(messages.noTitle);
            return aTempTitle.localeCompare(bTempTitle);
          };
        case ElasticSearchDocumentHeaderKeys.DATE:
          return (a, b) => {
            const aUpdatedMillisec = new Date(a.updated).getTime();
            const bUpdatedMillisec = new Date(b.updated).getTime();
            return aUpdatedMillisec - bUpdatedMillisec;
          };
        case ElasticSearchDocumentHeaderKeys.STATUS:
          return (a, b) => {
            return a?.status.localeCompare(b?.status);
          };
        default:
          break;
      }
    },
    [t]
  );

  const getSortedFiltered = useCallback(
    (sortedFilter = sort, filterWord, currentData) => {
      let sorted = currentData.slice();

      switch (sortedFilter.direction) {
        case ColumnDirections.ASC:
          sorted = sorted.sort(getSortFunction(sortedFilter.key));
          break;
        case ColumnDirections.DESC:
          sorted = sorted.sort(getSortFunction(sortedFilter.key)).reverse();
          break;
        default:
          break;
      }

      return sorted.filter(item =>
        typeof item.filename === 'string'
          ? item.filename.toLowerCase().includes(filterWord.toLowerCase())
          : true
      );
    },
    [getSortFunction, sort]
  );

  const onSearchChange = evt => {
    if (typeof evt?.target?.value !== 'string') {
      return;
    }

    const filterWord = evt.target.value;
    let sortedFiltered = allRows;
    if (filterWord !== '') {
      sortedFiltered = getSortedFiltered(sort, filterWord, allRows);
    }

    setDisplayedRows(sortedFiltered);
    setFilterWord(filterWord);
  };

  const changePage = pageCallback => {
    const page = Math.max(pageCallback.page, 1);
    const start = (page - 1) * pageCallback.pageSize;
    const end = start + pageCallback.pageSize;
    const data = displayedRows;

    setDisplayedRows(data.slice(start, end));
    setPage(pageCallback.page);
    setRowsPerPage(pageCallback.pageSize);
  };

  const getRangeText = (min, max) => {
    const tableLength = displayedRows?.length || 0;
    return `Showing ${min}–${max} of ${tableLength} items`;
  };

  return (
    <div className="ElasticSearchDocuments">
      {(deleteLoading || listFilesLoading) && (
        <DataTableSkeleton
          headers={headers}
          columnCount={4}
          rowCount={5}
          showHeader={false}
          showToolbar={true}
        />
      )}
      {!deleteLoading && !listFilesLoading && (
        <DataTable
          rows={displayedRows || []}
          headers={headers}
          sortRow={() => 0}
          isSortable
          render={({
            rows,
            headers,
            getSelectionProps,
            getBatchActionProps
          }) => (
            <TableContainer>
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    value={filterWord}
                    onChange={onSearchChange}
                    placeholder={t(messages.filterFilename)}
                    onClick={null}
                  />
                  <Button
                    kind="primary"
                    onClick={showPanel}
                    disabled={readOnly || uploadPanel}>
                    <FormattedMessage {...messages.upload} />
                  </Button>
                </TableToolbarContent>
                <TableBatchActions
                  {...getBatchActionProps()}
                  shouldShowBatchActions={selectedData.length > 0}
                  totalSelected={selectedData.length}
                  onCancel={() => setSelectedData([])}>
                  <TableBatchAction
                    kind="primary"
                    disabled={readOnly}
                    renderIcon={TrashCan}
                    onClick={batchDelete}>
                    {t(messages.delete)}
                  </TableBatchAction>
                </TableBatchActions>
              </TableToolbar>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableSelectAll
                      {...getSelectionProps()}
                      disabled={readOnly}
                      onSelect={handleSelectAll}
                      indeterminate={
                        selectedData.length > 0 &&
                        !(selectedData.length === displayedRows.length)
                      }
                      checked={
                        selectedData.length > 0 &&
                        selectedData.length === displayedRows.length
                      }
                    />
                    {headers.map(header => (
                      <TableHeader
                        key={header.key}
                        isSortable={header.isSortable}
                        isSortHeader={header.isSorted}
                        onClick={header.onClick}
                        // @ts-ignore
                        sortDirection={header.sortDirection}
                        className={`ElasticSearchDocuments__table-row-${header.key}`}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIdx) => (
                    <TableRow
                      key={rowIdx}
                      // {...getRowProps({ row })}
                      onClick={() => {}}>
                      <TableSelectRow
                        {...getSelectionProps({ row })}
                        onSelect={() => handleRowSelect(row)}
                        checked={selectedData?.some(
                          i =>
                            i === row.id &&
                            !row.id.startsWith(
                              ElasticSearchDocumentHeaderKeys.TITLE
                            )
                        )}
                      />
                      {row.cells.map((cell, idx) => (
                        <TableCell
                          key={cell.id}
                          className={`ElasticSearchDocuments__table-row-${cell.info.header}`}>
                          {cell.value}
                          {cell.info.header ===
                            ElasticSearchDocumentHeaderKeys.OVERFLOW && (
                            <div data-floating-menu-container>
                              <OverflowMenu
                                ariaLabel={t(messages.options)}
                                id={`ElasticSearchDocuments__overflow-${rowIdx}`}
                                iconDescription={t(messages.options)}
                                flipped={true}
                                direction={showOverflowOnTop ? 'top' : 'bottom'}
                                className={cx('overflow-' + idx, {
                                  'show-on-top': showOverflowOnTop
                                })}
                                menuOptionsClass="ElasticSearchDocuments-overflow-menu"
                                onClick={evt => setMenuPlacement(evt)}
                                onKeyPress={evt => setMenuPlacement(evt)}>
                                <OverflowMenuItem
                                  disabled={readOnly}
                                  requireTitle={false}
                                  hasDivider={true}
                                  isDelete={true}
                                  onClick={() => onDelete(row)}
                                  itemText={t(messages.delete)}
                                />
                              </OverflowMenu>
                            </div>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {filterWord === '' && rows.length === 0 && (
                    <TableRow className="ElasticSearchDocuments__empty">
                      <TableCell>
                        <NoDataEmptyState
                          title={t(messages.noDocuments)}
                          subtitle={t(messages.noDocumentsDescription)}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        />
      )}
      <Pagination
        className="ElasticSearchDocuments__pagination"
        totalItems={allRows.length}
        page={page}
        pageSize={rowsPerPage}
        pageSizes={ROWS_PER_PAGE_OPTIONS}
        onChange={changePage}
        itemRangeText={getRangeText}
        pageRangeText={(page, total) => `${page} of ${total} pages`}
      />
    </div>
  );
};
